import { useState, useEffect, useCallback, useRef, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authorsApi } from '../api/authors';
import { useDebounce } from '../hooks/useDebounce';
import AuthorAvatar from '../components/AuthorAvatar';
import { useAuthorSearch } from '../hooks/useAuthorSearch';
import { useToast } from '../contexts/ToastContext';
import {
  UserIcon,
  PencilIcon,
  TrashIcon,
  XCircleIcon,
  GlobeAltIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { XIcon, GitHubIcon, LinkedInIcon, BlueskyIcon, WebsiteIcon } from '../components/SocialIcons';

// Simple search bar component that uses the search hook's handlers
const AuthorSearchBar = memo(({ searchQuery, onSearchChange, isLoading, debouncedQuery }) => {
  const searchInputRef = useRef(null);

  return (
    <div className="mb-6">
      <div className="max-w-md">
        <label htmlFor="search" className="sr-only">
          Search authors
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {isLoading && debouncedQuery !== searchQuery ? (
              <svg className="h-5 w-5 text-obn-blue animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            )}
          </div>
          <input
            ref={searchInputRef}
            id="search"
            name="search"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-900 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
            placeholder="Search authors by name or alias..."
            type="search"
            value={searchQuery}
            onChange={onSearchChange}
            autoComplete="off"
          />
        </div>
      </div>
    </div>
  );
});

AuthorSearchBar.displayName = 'AuthorSearchBar';

// Separate component that handles all search functionality
const AuthorsSearchSection = memo(() => {
  const {
    searchQuery,
    debouncedSearchQuery,
    authorsData,
    isLoadingAuthors,
    handleSearchChange,
    handleClearSearch,
    handleLoadMore,
    hasNextPage,
    isFetchingNextPage,
    error
  } = useAuthorSearch();

  const navigate = useNavigate();

  const handleAuthorClick = useCallback((authorId) => {
    navigate(`/authors/${authorId}`);
  }, [navigate]);

  return (
    <>
      <AuthorSearchBar
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        isLoading={isLoadingAuthors}
        debouncedQuery={debouncedSearchQuery}
      />
      <OptimizedAuthorsList
        authorsData={authorsData}
        isLoading={isLoadingAuthors}
        debouncedSearchQuery={debouncedSearchQuery}
        searchQuery={searchQuery}
        onAuthorClick={handleAuthorClick}
        onClearSearch={handleClearSearch}
        onLoadMore={handleLoadMore}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        error={error}
      />
    </>
  );
});

AuthorsSearchSection.displayName = 'AuthorsSearchSection';

// Memoized authors list component with pagination
const AuthorsList = memo(({ 
  authorsData, 
  isLoading, 
  debouncedSearchQuery, 
  searchQuery, 
  onAuthorClick, 
  onClearSearch,
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
  error
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 shadow dark:ring-1 dark:ring-white/10 overflow-hidden sm:rounded-md">
      {/* Search status */}
      {isLoading && debouncedSearchQuery !== searchQuery && (
        <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          Searching for "{searchQuery}"...
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="px-4 py-3 text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border-b border-red-200 dark:border-red-800/50">
          Error loading authors: {error.message}
        </div>
      )}

      <ul className="divide-y divide-gray-200 dark:divide-gray-700">
        {authorsData?.authors?.map((author) => (
          <li key={author.id}>
            <div
              className="px-4 py-4 sm:px-6 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200"
              onClick={() => onAuthorClick(author.id)}
            >
              <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <AuthorAvatar 
                  name={author.name}
                  avatar={author.avatar}
                  size="w-10 h-10"
                  textSize="text-sm"
                />
                  <div>
                    <p className="text-sm font-medium text-obn-blue dark:text-blue-400">{author.name}</p>
                    {author.alias && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">@{author.alias}</p>
                    )}
                    {author.company && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">{author.company}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {author.xHandle && (
                    <div className="p-1 bg-gray-50 dark:bg-gray-700 rounded" title="Has X profile">
                      <XIcon className="h-3 w-3 text-gray-700 dark:text-gray-200" />
                    </div>
                  )}
                  {author.github && (
                    <div className="p-1 bg-gray-50 dark:bg-gray-700 rounded" title="Has GitHub profile">
                      <GitHubIcon className="h-3 w-3 text-gray-700 dark:text-gray-200" />
                    </div>
                  )}
                  {author.linkedinProfile && (
                    <div className="p-1 bg-blue-50 dark:bg-blue-900/40 rounded" title="Has LinkedIn profile">
                      <LinkedInIcon className="h-3 w-3 text-blue-600 dark:text-blue-300" />
                    </div>
                  )}
                  {author.blueskyHandle && (
                    <div className="p-1 bg-sky-50 dark:bg-sky-900/40 rounded" title="Has Bluesky profile">
                      <BlueskyIcon className="h-3 w-3 text-sky-600 dark:text-sky-300" />
                    </div>
                  )}
                  {author.website && (
                    <div className="p-1 bg-gray-50 dark:bg-gray-700 rounded" title="Has website">
                      <WebsiteIcon className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-2 flex items-center flex-wrap text-xs text-gray-500 dark:text-gray-400 space-x-4">
                <span>ID: {author.id}</span>
                <span>Created: {new Date(author.created).toLocaleDateString()}</span>
                {author.email && (
                  <span className="flex items-center">
                    <EnvelopeIcon className="h-3 w-3 mr-1 text-gray-400 dark:text-gray-500" />
                    {author.email}
                  </span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {/* Load More Button */}
      {hasNextPage && (
        <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <button
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
            className="w-full flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-obn-blue dark:text-blue-400 dark:hover:text-blue-300 bg-obn-lightblue hover:bg-obn-lightblue/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obn-blue disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {isFetchingNextPage ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading more authors...
              </>
            ) : (
              'Load More Authors'
            )}
          </button>
        </div>
      )}

      {/* Empty state */}
      {(!authorsData?.authors || authorsData.authors.length === 0) && !isLoading && !error && (
        <div className="text-center py-12">
          <div className="flex justify-center mb-4">
            <UserIcon className="h-16 w-16 text-gray-400 dark:text-gray-500" />
          </div>
          <p className="text-gray-500 dark:text-gray-400">
            {debouncedSearchQuery ? 'No authors found matching your search.' : 'No authors yet. Create your first author!'}
          </p>
          {debouncedSearchQuery && (
            <button
              onClick={onClearSearch}
              className="mt-2 text-obn-blue dark:text-blue-400 dark:hover:text-blue-300 hover:text-obn-blue/80 text-sm"
            >
              Clear search
            </button>
          )}
        </div>
      )}

      {/* Results count */}
      {authorsData?.authors && authorsData.authors.length > 0 && (
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 text-center">
          Showing {authorsData.authors.length} author{authorsData.authors.length !== 1 ? 's' : ''}
          {hasNextPage && ' • More available'}
        </div>
      )}
    </div>
  );
});

AuthorsList.displayName = 'AuthorsList';

// Custom comparison function for AuthorsList memo
// Only re-render if meaningful props change, ignore searchQuery differences when debouncedSearchQuery hasn't changed
const authorsListAreEqual = (prevProps, nextProps) => {
  // If debounced query changed, always re-render (new API call)
  if (prevProps.debouncedSearchQuery !== nextProps.debouncedSearchQuery) {
    return false;
  }
  
  // If loading state changed, always re-render
  if (prevProps.isLoading !== nextProps.isLoading) {
    return false;
  }
  
  // If authors data changed, always re-render
  if (prevProps.authorsData !== nextProps.authorsData) {
    return false;
  }
  
  // If callback functions changed, always re-render (though they shouldn't with useCallback)
  if (prevProps.onAuthorClick !== nextProps.onAuthorClick || prevProps.onClearSearch !== nextProps.onClearSearch) {
    return false;
  }
  
  // Ignore searchQuery differences when debouncedSearchQuery hasn't changed
  // This prevents re-renders during typing when the user is still within the debounce window
  return true;
};

// Create an optimized version of AuthorsList with custom comparison
const OptimizedAuthorsList = memo(AuthorsList, authorsListAreEqual);

function AuthorsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { authorId } = useParams();
  const { showSuccess, showError } = useToast();
  const [isCreating, setIsCreating] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    alias: '',
    email: '',
    avatar: '',
    website: '',
    xHandle: '',
    linkedinProfile: '',
    blueskyHandle: '',
    github: '',
    company: '',
  });


  // Query for deep-linked author
  const { data: deepLinkedAuthor, isLoading: isLoadingDeepLinkedAuthor } = useQuery({
    queryKey: ['author', authorId],
    queryFn: () => authorsApi.get(authorId),
    enabled: !!authorId && !editingAuthor,
  });


  // Handle deep link: when author is loaded from URL, set it as viewing
  useEffect(() => {
    if (deepLinkedAuthor && authorId && !editingAuthor) {
      setEditingAuthor(deepLinkedAuthor);
      setIsEditMode(false); // Default to view mode
      setFormData({
        name: deepLinkedAuthor.name || '',
        alias: deepLinkedAuthor.alias || '',
        email: deepLinkedAuthor.email || '',
        avatar: deepLinkedAuthor.avatar || '',
        website: deepLinkedAuthor.website || '',
        xHandle: deepLinkedAuthor.xHandle || '',
        linkedinProfile: deepLinkedAuthor.linkedinProfile || '',
        blueskyHandle: deepLinkedAuthor.blueskyHandle || '',
        github: deepLinkedAuthor.github || '',
        company: deepLinkedAuthor.company || '',
      });
    }
  }, [deepLinkedAuthor, authorId, editingAuthor]);

  const createMutation = useMutation({
    mutationFn: (data) => authorsApi.create(data),
    onSuccess: (newAuthor) => {
      // Add the new author to the individual author cache
      queryClient.setQueryData(['author', newAuthor.id], newAuthor);
      
      // Add the new author to the cache
      // Update all authors cache entries since we don't know the exact search query
      const authorsCacheEntries = queryClient.getQueryCache().findAll(['authors']);
      authorsCacheEntries.forEach((cacheEntry) => {
        if (cacheEntry.queryKey && cacheEntry.queryKey[0] === 'authors') {
          queryClient.setQueryData(cacheEntry.queryKey, (oldData) => {
            if (!oldData?.authors) return { authors: [newAuthor] };
            return {
              ...oldData,
              authors: [newAuthor, ...oldData.authors], // Add to beginning of list
            };
          });
        }
      });
      
      // Invalidate queries to ensure consistency
      queryClient.invalidateQueries(['authors']);
      
      setIsCreating(false);
      setFormData({
        name: '',
        alias: '',
        email: '',
        avatar: '',
        website: '',
        xHandle: '',
        linkedinProfile: '',
        blueskyHandle: '',
        github: '',
        company: '',
      });
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Failed to create author');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }) => authorsApi.update(id, data),
    onMutate: async (variables) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries(['author', authorId]);
      await queryClient.cancelQueries(['authors']);

      // Snapshot the previous values
      const previousAuthor = editingAuthor;
      const previousFormData = formData;

      // Optimistically update the local state
      // Extract id from variables since it's not part of the author data
      const { id, ...updateData } = variables;
      const optimisticUpdate = {
        ...editingAuthor,
        ...updateData,
        updatedAt: new Date().toISOString(), // Update timestamp optimistically
      };
      setEditingAuthor(optimisticUpdate);

      // Return a context object with the snapshotted values
      return { previousAuthor, previousFormData, optimisticUpdate };
    },
    onSuccess: (updatedAuthor, variables, context) => {
      // Update with the real data from the server
      setEditingAuthor(updatedAuthor);
      
      // Update the form data with the fresh data (handles null values)
      setFormData({
        name: updatedAuthor.name || '',
        alias: updatedAuthor.alias || '',
        email: updatedAuthor.email || '',
        avatar: updatedAuthor.avatar || '',
        website: updatedAuthor.website || '',
        xHandle: updatedAuthor.xHandle || '',
        linkedinProfile: updatedAuthor.linkedinProfile || '',
        blueskyHandle: updatedAuthor.blueskyHandle || '',
        github: updatedAuthor.github || '',
        company: updatedAuthor.company || '',
      });
      
      // Update the individual author cache entry
      queryClient.setQueryData(['author', updatedAuthor.id], updatedAuthor);
      
      // Update the authors list cache with the updated author
      // Update all possible cache entries for authors since we don't know the exact search query
      const authorsCacheEntries = queryClient.getQueryCache().findAll(['authors']);
      authorsCacheEntries.forEach((cacheEntry) => {
        if (cacheEntry.queryKey && cacheEntry.queryKey[0] === 'authors') {
          queryClient.setQueryData(cacheEntry.queryKey, (oldData) => {
            if (!oldData?.authors) return oldData;
            return {
              ...oldData,
              authors: oldData.authors.map(author => 
                author.id === updatedAuthor.id ? updatedAuthor : author
              ),
            };
          });
        }
      });
      
      setIsEditMode(false); // Exit edit mode but stay on author page
    },
    onError: (error, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousAuthor) {
        setEditingAuthor(context.previousAuthor);
        setFormData(context.previousFormData);
      }
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update author';
      showError(`Update failed: ${errorMessage}`);
    },
    onSettled: () => {
      // Always refetch after error or success to ensure data consistency
      queryClient.invalidateQueries(['authors']);
      queryClient.invalidateQueries(['author', authorId]);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: authorsApi.delete,
    onMutate: async (authorIdToDelete) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries(['authors']);
      await queryClient.cancelQueries(['author', authorId]);

      // Snapshot the previous values from all cache entries
      const previousAuthorsData = {};
      const authorsCacheEntries = queryClient.getQueryCache().findAll(['authors']);
      
      // Store snapshot of individual author cache entry
      const individualAuthorData = queryClient.getQueryData(['author', authorIdToDelete]);
      
      // Store snapshots and optimistically remove the author from all cache entries
      authorsCacheEntries.forEach((cacheEntry) => {
        if (cacheEntry.queryKey && cacheEntry.queryKey[0] === 'authors') {
          previousAuthorsData[cacheEntry.queryKey.join(':')] = cacheEntry.state.data;
          
          queryClient.setQueryData(cacheEntry.queryKey, (oldData) => {
            if (!oldData?.authors) return oldData;
            return {
              ...oldData,
              authors: oldData.authors.filter(author => author.id !== authorIdToDelete),
            };
          });
        }
      });
      
      // Remove the individual author cache entry
      queryClient.removeQueries(['author', authorIdToDelete]);

      // Return context for rollback
      return { previousAuthorsData, individualAuthorData };
    },
    onSuccess: (data, authorIdToDelete) => {
      if (authorId) {
        navigate('/authors'); // Navigate back to list after deletion
      }
    },
    onError: (error, authorIdToDelete, context) => {
      // Rollback on error
      if (context?.previousAuthorsData) {
        Object.entries(context.previousAuthorsData).forEach(([queryKeyString, data]) => {
          const queryKey = queryKeyString.split(':');
          queryClient.setQueryData(queryKey, data);
        });
      }

      // Restore individual author cache entry
      if (context?.individualAuthorData) {
        queryClient.setQueryData(['author', authorIdToDelete], context.individualAuthorData);
      }
      showError(error.response?.data?.error || 'Failed to delete author');
    },
    onSettled: () => {
      // Ensure data consistency
      queryClient.invalidateQueries(['authors']);
      queryClient.invalidateQueries(['author', authorId]);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    // Client-side validation
    if (!formData.name?.trim()) {
      showError('Name is required');
      return;
    }

    if (editingAuthor) {
      updateMutation.mutate({ id: editingAuthor.id, ...formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleCancel = () => {
    if (isEditMode) {
      // Cancel editing, return to view mode
      setIsEditMode(false);
      // Reset form data to original author data
      if (editingAuthor) {
        setFormData({
          name: editingAuthor.name || '',
          alias: editingAuthor.alias || '',
          email: editingAuthor.email || '',
          avatar: editingAuthor.avatar || '',
          website: editingAuthor.website || '',
          xHandle: editingAuthor.xHandle || '',
          linkedinProfile: editingAuthor.linkedinProfile || '',
          blueskyHandle: editingAuthor.blueskyHandle || '',
          github: editingAuthor.github || '',
          company: editingAuthor.company || '',
        });
      }
    } else {
      // Cancel creation or navigate back
      setIsCreating(false);
      setEditingAuthor(null);
      setFormData({
        name: '',
        alias: '',
        email: '',
        avatar: '',
        website: '',
        xHandle: '',
        linkedinProfile: '',
        blueskyHandle: '',
        github: '',
        company: '',
      });

      // If we came from a deep link, navigate back to authors list
      if (authorId) {
        navigate('/authors');
      }
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this author? This action cannot be undone.')) {
      deleteMutation.mutate(editingAuthor.id);
    }
  };

  // No search-related callbacks needed - handled in AuthorsSearchSection

  if (authorId && isLoadingDeepLinkedAuthor) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500 dark:text-gray-400">
          Loading author...
        </div>
      </div>
    );
  }

  // Handle case where deep-linked author is not found
  if (authorId && !isLoadingDeepLinkedAuthor && !deepLinkedAuthor && !editingAuthor) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center py-12">
          <div className="flex justify-center mb-4">
            <XCircleIcon className="h-16 w-16 text-red-400" />
          </div>
          <h2 className="text-lg font-medium text-obn-blue dark:text-white mb-2">Author Not Found</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-4">The requested author could not be found.</p>
          <button
            onClick={() => navigate('/authors')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-obn-blue hover:bg-obn-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obn-blue"
          >
            ← Back to Authors
          </button>
        </div>
      </div>
    );
  }

  // When viewing/editing an author
  if (editingAuthor) {
    // Generate social links manually since API returns plain data, not model instances
    // Handle null values from database
    const socialLinks = {};
    if (editingAuthor.xHandle && editingAuthor.xHandle !== null) {
      socialLinks.x = `https://x.com/${editingAuthor.xHandle}`;
    }
    if (editingAuthor.github && editingAuthor.github !== null) {
      socialLinks.github = `https://github.com/${editingAuthor.github}`;
    }
    if (editingAuthor.blueskyHandle && editingAuthor.blueskyHandle !== null) {
      socialLinks.bluesky = `https://bsky.app/profile/${editingAuthor.blueskyHandle}`;
    }
    if (editingAuthor.linkedinProfile && editingAuthor.linkedinProfile !== null) {
      socialLinks.linkedin = editingAuthor.linkedinProfile;
    }
    if (editingAuthor.website && editingAuthor.website !== null) {
      socialLinks.website = editingAuthor.website;
    }

    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-obn-blue dark:text-white">
              {isEditMode ? 'Edit Author' : 'Author Details'}
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{editingAuthor.name}</p>
          </div>
          <div className="flex items-center space-x-3">
            {!isEditMode && (
              <>
                <button
                  onClick={() => setIsEditMode(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-obn-blue hover:bg-obn-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obn-blue"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Edit
                </button>
                <button
                  onClick={handleDelete}
                  className="inline-flex items-center px-4 py-2 border border-obn-red/30 text-sm font-medium rounded-md text-obn-red bg-white dark:bg-gray-800 hover:bg-obn-red/5 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obn-red"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Delete
                </button>
              </>
            )}
            <button
              onClick={handleCancel}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obn-blue"
            >
              ← Back to Authors
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow dark:ring-1 dark:ring-white/10 rounded-lg p-6">
          {isEditMode ? (
            // Edit Mode - Form
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="alias" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Alias/Username
                  </label>
                  <input
                    type="text"
                    name="alias"
                    id="alias"
                    value={formData.alias}
                    onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
                    className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                    placeholder="URL-friendly identifier"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Company
                  </label>
                  <input
                    type="text"
                    name="company"
                    id="company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Website
                </label>
                <input
                  type="url"
                  name="website"
                  id="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                  placeholder="https://example.com"
                />
              </div>

              <div>
                <label htmlFor="avatar" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Avatar URL
                </label>
                <input
                  type="url"
                  name="avatar"
                  id="avatar"
                  value={formData.avatar}
                  onChange={(e) => setFormData({ ...formData, avatar: e.target.value })}
                  className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                  placeholder="https://example.com/avatar.jpg"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="xHandle" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    X (Twitter) Handle
                  </label>
                  <input
                    type="text"
                    name="xHandle"
                    id="xHandle"
                    value={formData.xHandle}
                    onChange={(e) => setFormData({ ...formData, xHandle: e.target.value })}
                    className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                    placeholder="username (without @)"
                  />
                </div>

                <div>
                  <label htmlFor="github" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    GitHub Username
                  </label>
                  <input
                    type="text"
                    name="github"
                    id="github"
                    value={formData.github}
                    onChange={(e) => setFormData({ ...formData, github: e.target.value })}
                    className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                    placeholder="username"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="blueskyHandle" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Bluesky Handle
                  </label>
                  <input
                    type="text"
                    name="blueskyHandle"
                    id="blueskyHandle"
                    value={formData.blueskyHandle}
                    onChange={(e) => setFormData({ ...formData, blueskyHandle: e.target.value })}
                    className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                    placeholder="username.bsky.social"
                  />
                </div>

                <div>
                  <label htmlFor="linkedinProfile" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    LinkedIn Profile URL
                  </label>
                  <input
                    type="url"
                    name="linkedinProfile"
                    id="linkedinProfile"
                    value={formData.linkedinProfile}
                    onChange={(e) => setFormData({ ...formData, linkedinProfile: e.target.value })}
                    className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                    placeholder="https://linkedin.com/in/username"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obn-blue"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-obn-blue hover:bg-obn-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obn-blue disabled:opacity-50"
                >
                  Update Author
                </button>
              </div>
            </form>
          ) : (
            // Display Mode - Read-only view
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Full Name</h3>
                  <p className="text-lg text-gray-900 dark:text-gray-100">{editingAuthor.name}</p>
                </div>

                {editingAuthor.alias && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Alias</h3>
                    <p className="text-lg text-gray-900 dark:text-gray-100">{editingAuthor.alias}</p>
                  </div>
                )}
              </div>

              {(editingAuthor.email || editingAuthor.company) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {editingAuthor.email && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Email</h3>
                      <p className="text-gray-900 dark:text-gray-100">{editingAuthor.email}</p>
                    </div>
                  )}

                  {editingAuthor.company && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Company</h3>
                      <p className="text-gray-900 dark:text-gray-100">{editingAuthor.company}</p>
                    </div>
                  )}
                </div>
              )}

              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Avatar</h3>
                <AuthorAvatar 
                  name={editingAuthor.name}
                  avatar={editingAuthor.avatar}
                  size="w-16 h-16"
                  textSize="text-lg"
                />
              </div>

              {Object.keys(socialLinks).length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Social Media & Links</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {socialLinks.website && (
                      <a
                        href={socialLinks.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <WebsiteIcon className="h-4 w-4 mr-2" />
                        Website
                      </a>
                    )}
                    {socialLinks.x && (
                      <a
                        href={socialLinks.x}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <XIcon className="h-4 w-4 mr-2" />
                        X (Twitter)
                      </a>
                    )}
                    {socialLinks.github && (
                      <a
                        href={socialLinks.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <GitHubIcon className="h-4 w-4 mr-2" />
                        GitHub
                      </a>
                    )}
                    {socialLinks.linkedin && (
                      <a
                        href={socialLinks.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <LinkedInIcon className="h-4 w-4 mr-2" />
                        LinkedIn
                      </a>
                    )}
                    {socialLinks.bluesky && (
                      <a
                        href={socialLinks.bluesky}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <BlueskyIcon className="h-4 w-4 mr-2" />
                        Bluesky
                      </a>
                    )}
                  </div>
                </div>
              )}

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                  <p><span className="font-medium">Created:</span> {new Date(editingAuthor.created).toLocaleString()}</p>
                  <p><span className="font-medium">Updated:</span> {new Date(editingAuthor.modified).toLocaleString()}</p>
                  <p><span className="font-medium">ID:</span> {editingAuthor.id}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-obn-blue dark:text-white">Authors</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Manage author profiles and their details</p>
        </div>
        {!isCreating && (
          <button
            onClick={() => setIsCreating(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-obn-blue hover:bg-obn-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obn-blue"
          >
            <UserIcon className="h-4 w-4 mr-2" />
            Create Author
          </button>
        )}
      </div>

      {/* Search Section - isolated component with its own state */}
      {!isCreating && <AuthorsSearchSection />}

      {isCreating && (
        <div className="bg-white dark:bg-gray-800 shadow dark:ring-1 dark:ring-white/10 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-obn-blue dark:text-white mb-4">
            Create New Author
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="alias" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Alias/Username
                </label>
                <input
                  type="text"
                  name="alias"
                  id="alias"
                  value={formData.alias}
                  onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
                  className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                  placeholder="URL-friendly identifier"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Company
                </label>
                <input
                  type="text"
                  name="company"
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="website" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Website
              </label>
              <input
                type="url"
                name="website"
                id="website"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                placeholder="https://example.com"
              />
            </div>

            <div>
              <label htmlFor="avatar" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Avatar URL
              </label>
              <input
                type="url"
                name="avatar"
                id="avatar"
                value={formData.avatar}
                onChange={(e) => setFormData({ ...formData, avatar: e.target.value })}
                className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                placeholder="https://example.com/avatar.jpg"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="xHandle" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  X (Twitter) Handle
                </label>
                <input
                  type="text"
                  name="xHandle"
                  id="xHandle"
                  value={formData.xHandle}
                  onChange={(e) => setFormData({ ...formData, xHandle: e.target.value })}
                  className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                  placeholder="username (without @)"
                />
              </div>

              <div>
                <label htmlFor="github" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  GitHub Username
                </label>
                <input
                  type="text"
                  name="github"
                  id="github"
                  value={formData.github}
                  onChange={(e) => setFormData({ ...formData, github: e.target.value })}
                  className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                  placeholder="username"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="blueskyHandle" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Bluesky Handle
                </label>
                <input
                  type="text"
                  name="blueskyHandle"
                  id="blueskyHandle"
                  value={formData.blueskyHandle}
                  onChange={(e) => setFormData({ ...formData, blueskyHandle: e.target.value })}
                  className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                  placeholder="username.bsky.social"
                />
              </div>

              <div>
                <label htmlFor="linkedinProfile" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  LinkedIn Profile URL
                </label>
                <input
                  type="url"
                  name="linkedinProfile"
                  id="linkedinProfile"
                  value={formData.linkedinProfile}
                  onChange={(e) => setFormData({ ...formData, linkedinProfile: e.target.value })}
                  className="mt-1 block w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                  placeholder="https://linkedin.com/in/username"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obn-blue"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-obn-blue hover:bg-obn-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obn-blue disabled:opacity-50"
              >
                Create Author
              </button>
            </div>
          </form>
        </div>
      )}

    </div>
  );
}

export default AuthorsPage;
